import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { useKeenSlider } from "keen-slider/react";
import { useCallback, VFC } from "react";
import styled from "styled-components";
import { useAnchor } from "hooks";
import { Carousel3Content } from "types/graphql/generated";
import { Carousel3Button, Employee } from "./components";

interface Props {
  content: Carousel3Content;
}

const Carousel3: VFC<Props> = withErrorBoundary(({ content: { title, carousel3Persons } }) => {
  const [sliderRef, instanceRef] = useKeenSlider({
    loop: true,
    slides: {
      perView: 2,
      spacing: 32,
    },
    breakpoints: {
      "(max-width: 576px)": {
        slides: {
          perView: 1,
          spacing: 24,
        },
      },
    },
  });

  const handleClickPrevButton = useCallback(() => instanceRef.current?.prev(), [instanceRef]);
  const handleClickNextButton = useCallback(() => instanceRef.current?.next(), [instanceRef]);

  useAnchor("carousel3");

  return (
    <StyledCareers3 className="[ carousel3 ] Content">
      <h2 className="[ Carousel3__title ] [ Font_48_54_600 lg:Font_40_48_600 sm:Font_32_40_600 ]">{title}</h2>
      <div className="[ Carousel3__sliderWithDesktopButtonsContainer ]">
        <Carousel3Button
          className="[ Carousel3__arrowButtonLeft ]"
          onClick={handleClickPrevButton}
          aria-label="button-left"
        />
        <div className="[ Carousel3__keenSlider ] [ keen-slider ]" ref={sliderRef}>
          {carousel3Persons.map((employee) => (
            <div key={employee.id} className="[ Carousel3__slide ] [ keen-slider__slide ]">
              <Employee content={employee} />
            </div>
          ))}
        </div>
        <Carousel3Button
          className="[ Carousel3__arrowButtonRight ]"
          onClick={handleClickNextButton}
          aria-label="button-right"
          direction="right"
        />
      </div>
      <div className="[ Carousel3__buttonsBottom ]">
        <Carousel3Button onClick={handleClickPrevButton} aria-label="button-left" />
        <Carousel3Button
          className="[ Carousel3__arrowRight_bottom ]"
          onClick={handleClickNextButton}
          aria-label="button-left"
          direction="right"
        />
      </div>
    </StyledCareers3>
  );
}, {});

const StyledCareers3 = styled.section`
  margin-top: -64px;
  padding-top: 64px;

  .Carousel3__arrowButtonLeft,
  .Carousel3__arrowButtonRight {
    margin-top: 150px;
    transform: translateY(-50%);
  }

  .Carousel3__arrowButtonLeft {
    margin-right: 32px;
  }

  .Carousel3__arrowButtonRight {
    margin-left: 32px;
  }

  .Carousel3__title {
    text-align: center;
  }

  .Carousel3__sliderWithDesktopButtonsContainer {
    display: flex;
    justify-content: space-between;
    margin-top: 64px;
  }

  .Carousel3__keenSlider {
    flex: 1;
    display: flex;
    overflow: hidden;
  }

  .Carousel3__buttonsBottom {
    display: none;
  }

  @media (max-width: 860px) {
    .Carousel3__arrowButtonLeft,
    .Carousel3__arrowButtonRight {
      display: none;
    }

    .Carousel3__sliderWithDesktopButtonsContainer {
      display: block;
      margin-top: 40px;
    }

    .Carousel3__keenSlider {
      overflow: visible;
    }

    .Carousel3__buttonsBottom {
      display: flex;
      justify-content: flex-end;
      margin-top: 40px;
    }

    .Carousel3__arrowRight_bottom {
      margin-left: 24px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    .Carousel3__sliderWithDesktopButtonsContainer {
      margin-top: 24px;
    }

    .Carousel3__buttonsBottom {
      margin-top: 24px;
    }
  }
`;

gql`
  fragment Carousel3Content on ComponentCarouselCarousel3 {
    title
    carousel3Persons {
      ...Carousel3EmployeeContent
    }
  }
`;

export { Carousel3 };
export type { Props as Carousel3Props };
